export type TelegramAuthPayload = {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
};

export type TelegramUser = {
  id: number;
  username?: string;
  firstName?: string;
  lastName?: string;
  photoUrl?: string;
};

export type PwaAuthUser = {
  id: number;
  username?: string;
  authType: 'telegram' | 'account';
};

export type AgentAuthUser = {
  id: number;
  agentName: string;
  authType: 'agent';
};

export type VerifiedAgent = {
  agentName: string;
  moltbookHandle?: string | null;
  createdAt?: string | null;
};

export type AgentStrategyConfig = {
  mode: 'paper' | 'live';
  cadenceMinutes: number;
  minSolBalance: number;
  maxTradeSol: number;
  maxDailyTrades: number;
  allowedMints: string[];
};

export type AgentStrategyResponse = {
  strategy: AgentStrategyConfig | null;
  enabled: boolean;
  updatedAt?: string | null;
};

export type TelegramPosition = {
  symbol: string;
  tokenAddress?: string;
  amount: number;
  usdValue: number;
  entryPriceUsd?: number;
  currentPriceUsd?: number;
  pnlUsd?: number;
};

export type TelegramGroupPortfolio = {
  chatId: number;
  groupName: string;
  walletAddress: string;
  totalUsdValue: number;
  totalSol: number;
  positions: TelegramPosition[];
};

export type TelegramPortfolio = {
  totalUsdValue: number;
  groups: TelegramGroupPortfolio[];
};

export type TelegramPwaWallet = {
  walletAddress: string;
  harvestWalletAddress?: string | null;
  telegramUserId?: number | null;
  telegramUsername?: string | null;
  accountId?: number | null;
  accountUsername?: string | null;
  authType?: 'telegram' | 'account' | null;
  createdAt?: string | null;
  created?: boolean;
};

export type AgentWallet = {
  walletAddress: string;
  harvestWalletAddress?: string | null;
  agentId?: number | null;
  agentName?: string | null;
  createdAt?: string | null;
  created?: boolean;
};

export type PwaWalletBalance = {
  balanceLamports: number;
  balanceSol: number;
};

export type PwaWithdrawResponse = {
  signature: string;
  amountSol: number;
  toAddress: string;
};

export type PwaSwapResponse = {
  signature: string;
  amountOut?: number | null;
  tokenSymbol?: string | null;
};

export type PwaTokenDeployResponse = {
  mintAddress: string;
  poolAddress?: string | null;
  positionMint?: string | null;
  mintSignature?: string | null;
  poolSignature?: string | null;
  lockSignature?: string | null;
  devBuyTokenAmount?: number | null;
  devBuySignature?: string | null;
  bundleFeeSignature?: string | null;
  massCallFeeSignature?: string | null;
  bundleBuyExecuted?: boolean;
  bundleBuyWalletCount?: number;
  bundleBuySignatures?: string[];
  bundleBuyResults?: Array<{ wallet: string; amountSol: number; tokenAmount: number; signature: string }>;
  bundleBuySkippedWallets?: string[];
  bundleBuyFailedWallets?: Array<{ wallet: string; error: string }>;
  bundleBuyTokenTotal?: number | null;
  massCallEnabled?: boolean;
  massCallSentCount?: number;
  massCallFailedCount?: number;
};

export type PwaHolding = {
  mintAddress: string;
  name?: string | null;
  symbol?: string | null;
  amount: number;
  decimals: number;
  logoUrl?: string | null;
  entryPriceUsd?: number | null;
};

export type PwaSwapEvent = {
  id: number | string;
  mintAddress: string;
  tokenSymbol?: string | null;
  direction: 'buy' | 'sell';
  solAmount?: number | null;
  amountIn?: string | null;
  tokenAmount?: number | null;
  signature?: string | null;
  timestamp?: string | null;
};

export type AgentSwapEvent = {
  id: number | string;
  mint_address: string;
  token_symbol?: string | null;
  direction: 'buy' | 'sell';
  sol_amount?: number | null;
  amount_in?: string | null;
  token_amount?: number | null;
  signature?: string | null;
  created_at?: string | null;
};

export type AgentTvlSummary = {
  totalValueUsd: number;
  totalMarketSizeUsd: number;
};

export type PwaHarvestPosition = {
  positionMint: string;
  poolAddress: string;
  tokenMint: string;
  label: string;
  logoUrl?: string | null;
  isLocked: boolean;
  lockType?: number;
};

export type PwaHarvestResult = {
  sig: string;
  harvestedA: number;
  harvestedB: number;
  mintA: string;
  mintB: string;
  label?: string | null;
  wsolTransferSig?: string | null;
  solTransferSig?: string | null;
  transferSigA?: string | null;
  transferSigB?: string | null;
};

export type PwaWsolBalance = {
  amountLamports: number;
  amountSol: number;
};

export type PwaUnwrapWsolResult = {
  signature: string;
  amountSol: number;
};

export type PwaBundleWalletsResult = {
  wallets: string[];
  details?: PwaBundleWalletDetail[];
};

export type PwaBundleWalletDetail = {
  walletAddress: string;
  walletIndex: number;
  solBalanceLamports: number;
  tokenHoldings: Array<{
    mint: string;
    amount: number;
    decimals: number;
    symbol?: string | null;
  }>;
};

export type PwaTokenBalance = {
  mint: string;
  amount: number;
  decimals: number;
};

export type PwaBundleWalletSellResult = {
  signature: string;
  tokensSold: number;
  solReceived: number;
  mint: string;
  symbol?: string | null;
};

export type PwaBundleWalletSellAllResult = {
  results: Array<{
    walletAddress: string;
    mint: string;
    symbol?: string | null;
    signature?: string;
    tokensSold?: number;
    solReceived?: number;
    error?: string;
  }>;
};

export type PwaBundleWalletWithdrawResult = {
  signature: string;
  amountSol: number;
  walletAddress: string;
};

export type PwaBundleWalletWithdrawAllResult = {
  results: Array<{
    walletAddress: string;
    signature?: string;
    amountSol?: number;
    error?: string;
  }>;
};

export type PwaBundleScaleOutRule = {
  mintAddress: string;
  targetMarketCapUsd: number;
  scaleOutPercent: 10 | 20 | 30;
  enabled: boolean;
};

export type PwaFeaturedSlot = {
  slotType: string;
  mintAddress: string;
  endsAt: string;
};

export type PwaFeaturedSlots = {
  primary: PwaFeaturedSlot | null;
  secondary: PwaFeaturedSlot[];
};

export type PwaCarouselSlots = {
  totalSlots: number;
  availableSlots: number;
  slots: Array<{
    mintAddress: string;
    endsAt: string;
  }>;
};

export type SnsAvailabilityResult = {
  name: string;
  domain: string;
  available: boolean;
  priceUsd: number;
  priceUsdc: number;
};

export type SnsRegisterResult = {
  domain: string;
  priceUsd: number;
  signature: string;
  swapSignature?: string | null;
};

const TELEGRAM_API_BASE =
  (import.meta.env as any).VITE_TELEGRAM_API_URL || '';
const MOLTING_API_BASE =
  (import.meta.env as any).VITE_MOLTING_API_URL || TELEGRAM_API_BASE;

const normalizeErrorMessage = (status: number, rawText: string): string => {
  const lowered = rawText.toLowerCase();
  if (status === 401) return 'Please sign in again.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'That item could not be found.';
  if (status === 409) return 'That request conflicts with an existing record.';
  if (lowered.includes('insufficient')) return 'Insufficient Funds!';
  if (lowered.includes('simulation failed')) return 'Transaction failed. Please try again.';
  if (lowered.includes('invalid credentials')) return 'Invalid username or password.';
  if (lowered.includes('username already')) return 'Username already taken.';
  if (lowered.includes('missing') || lowered.includes('invalid')) return 'Please check your inputs and try again.';
  return 'Something went wrong. Please try again.';
};

const requestJson = async <T>(input: RequestInfo, init?: RequestInit): Promise<T> => {
  const target =
    typeof input === 'string'
      ? input
      : (input as { url?: string })?.url || '';
  const isAgentRequest = target.includes('/api/agent/');
  const isPwaRequest = target.includes('/api/pwa/');
  const isTelegramRequest = target.includes('/api/telegram/');
  const sessionId =
    typeof window !== 'undefined'
      ? isAgentRequest
        ? window.localStorage.getItem('agent_session')
        : isPwaRequest
          ? window.localStorage.getItem('pwa_session')
          : isTelegramRequest
            ? window.localStorage.getItem('telegram_session')
            : window.localStorage.getItem('pwa_session') || window.localStorage.getItem('telegram_session')
      : null;
  const pwaSession =
    typeof window !== 'undefined' ? window.localStorage.getItem('pwa_session') : null;
  const telegramSession =
    typeof window !== 'undefined' ? window.localStorage.getItem('telegram_session') : null;
  const agentSession =
    typeof window !== 'undefined' ? window.localStorage.getItem('agent_session') : null;
  const headers = new Headers(init?.headers || {});
  if (sessionId) {
    headers.set('Authorization', `Bearer ${sessionId}`);
  }
  if (isPwaRequest && pwaSession) {
    headers.set('X-Pwa-Session', pwaSession);
  }
  if (isTelegramRequest && telegramSession) {
    headers.set('X-Telegram-Session', telegramSession);
  }
  if (isAgentRequest && agentSession) {
    headers.set('X-Agent-Session', agentSession);
  }

  const response = await fetch(input, {
    ...init,
    headers,
    credentials: 'include'
  });

  if (!response.ok) {
    const message = await response.text();
    const friendly = normalizeErrorMessage(response.status, message || '');
    throw new Error(friendly);
  }

  return response.json() as Promise<T>;
};

export function setTelegramSession(sessionId: string | null) {
  if (typeof window === 'undefined') return;
  if (sessionId) {
    window.localStorage.setItem('telegram_session', sessionId);
  } else {
    window.localStorage.removeItem('telegram_session');
  }
}

export function setPwaSession(sessionId: string | null) {
  if (typeof window === 'undefined') return;
  if (sessionId) {
    window.localStorage.setItem('pwa_session', sessionId);
  } else {
    window.localStorage.removeItem('pwa_session');
  }
}

export function setAgentSession(sessionId: string | null) {
  if (typeof window === 'undefined') return;
  if (sessionId) {
    window.localStorage.setItem('agent_session', sessionId);
  } else {
    window.localStorage.removeItem('agent_session');
  }
}

export async function registerPwaAccount(params: {
  username: string;
  password: string;
}): Promise<PwaAuthUser> {
  const result = await requestJson<{ success: boolean; user: PwaAuthUser; sessionId?: string }>(
    `${TELEGRAM_API_BASE}/api/pwa/account/register`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(params)
    }
  );

  if (result.sessionId) {
    setPwaSession(result.sessionId);
  }

  return result.user;
}

export async function loginPwaAccount(params: {
  username: string;
  password: string;
}): Promise<PwaAuthUser> {
  const result = await requestJson<{ success: boolean; user: PwaAuthUser; sessionId?: string }>(
    `${TELEGRAM_API_BASE}/api/pwa/account/login`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(params)
    }
  );

  if (result.sessionId) {
    setPwaSession(result.sessionId);
  }

  return result.user;
}

export async function registerAgentInit(params: {
  agentName: string;
}): Promise<{ status: string; sessionId?: string; registrationUrl?: string; expiresAt?: string }> {
  return requestJson(`${MOLTING_API_BASE}/agents/register/init`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ agent_name: params.agentName })
  });
}

export async function registerAgentComplete(params: {
  agentName: string;
  password: string;
  sessionId: string;
}): Promise<{ status: string; data?: { user?: AgentAuthUser; sessionId?: string } }> {
  return requestJson(`${MOLTING_API_BASE}/agents/register/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      agent_name: params.agentName,
      password: params.password,
      session_id: params.sessionId
    })
  });
}

export async function resetAgentPassword(params: {
  agentName: string;
  password: string;
}): Promise<{ status: string; data?: { user?: AgentAuthUser; sessionId?: string } }> {
  return requestJson(`${MOLTING_API_BASE}/agents/password/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      agent_name: params.agentName,
      password: params.password
    })
  });
}

export async function loginAgentAccount(params: {
  agentName: string;
  password: string;
}): Promise<AgentAuthUser> {
  const result = await requestJson<{ success: boolean; user: AgentAuthUser; sessionId?: string }>(
    `${TELEGRAM_API_BASE}/api/agent/account/login`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        agent_name: params.agentName,
        password: params.password
      })
    }
  );

  if (result.sessionId) {
    setAgentSession(result.sessionId);
  }

  return result.user;
}

async function fetchAgentAccountMe(): Promise<AgentAuthUser | null> {
  if (typeof window !== 'undefined' && !window.localStorage.getItem('agent_session')) {
    return null;
  }
  try {
    const result = await requestJson<{ success: boolean; user: AgentAuthUser }>(
      `${TELEGRAM_API_BASE}/api/agent/account/me`
    );
    return result.user;
  } catch {
    setAgentSession(null);
    return null;
  }
}

export async function logoutAgentAccount(): Promise<void> {
  try {
    await requestJson(`${TELEGRAM_API_BASE}/api/agent/account/logout`, {
      method: 'POST'
    });
  } catch {
    // Ignore logout errors
  } finally {
    setAgentSession(null);
  }
}

export async function fetchPwaAccountMe(): Promise<PwaAuthUser | null> {
  if (typeof window !== 'undefined' && !window.localStorage.getItem('pwa_session')) {
    return null;
  }
  try {
    const result = await requestJson<{ success: boolean; user: PwaAuthUser }>(
      `${TELEGRAM_API_BASE}/api/pwa/account/me`
    );
    return result.user;
  } catch (error: any) {
    if (error?.message?.toLowerCase?.().includes('sign in')) {
      setPwaSession(null);
    }
    return null;
  }
}

export async function logoutPwaAccount(): Promise<void> {
  try {
    await requestJson(`${TELEGRAM_API_BASE}/api/pwa/account/logout`, {
      method: 'POST'
    });
  } catch (error) {
    // Ignore logout errors
  } finally {
    setPwaSession(null);
  }
}

export async function fetchPwaAuthUser(): Promise<PwaAuthUser | TelegramUser | null> {
  const accountUser = await fetchPwaAccountMe();
  if (accountUser) {
    return accountUser;
  }
  return fetchTelegramMe();
}

export async function fetchAgentAuthUser(): Promise<AgentAuthUser | null> {
  return fetchAgentAccountMe();
}

export async function loginTelegram(
  payload: TelegramAuthPayload,
  options?: { handoffCode?: string }
): Promise<TelegramUser> {
  const result = await requestJson<{ success: boolean; user: TelegramUser; sessionId?: string }>(
    `${TELEGRAM_API_BASE}/api/telegram/login`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...payload,
        handoffCode: options?.handoffCode
      })
    }
  );

  if (result.sessionId) {
    setTelegramSession(result.sessionId);
  }

  return result.user;
}

export async function fetchTelegramMe(): Promise<TelegramUser | null> {
  if (typeof window !== 'undefined' && !window.localStorage.getItem('telegram_session')) {
    return null;
  }
  try {
    const result = await requestJson<{ success: boolean; user: TelegramUser }>(
      `${TELEGRAM_API_BASE}/api/telegram/me`
    );
    return result.user;
  } catch (error: any) {
    if (error?.message?.toLowerCase?.().includes('sign in')) {
      setTelegramSession(null);
    }
    return null;
  }
}

export async function fetchTelegramPortfolio(): Promise<TelegramPortfolio | null> {
  try {
    const result = await requestJson<{ success: boolean; data: TelegramPortfolio }>(
      `${TELEGRAM_API_BASE}/api/telegram/portfolio`
    );
    return result.data;
  } catch (error: any) {
    if (error?.message?.includes('401')) {
      return null;
    }
    return null;
  }
}

export async function ensurePwaWallet(): Promise<TelegramPwaWallet> {
  const result = await requestJson<{ success: boolean; data: TelegramPwaWallet }>(
    `${TELEGRAM_API_BASE}/api/pwa/wallet`,
    {
      method: 'POST'
    }
  );

  return result.data;
}

export async function fetchPwaWallet(): Promise<TelegramPwaWallet | null> {
  try {
    const result = await requestJson<{ success: boolean; data: TelegramPwaWallet }>(
      `${TELEGRAM_API_BASE}/api/pwa/wallet`
    );
    return result.data;
  } catch (error: any) {
    if (error?.message?.includes('401') || error?.message?.includes('404')) {
      return null;
    }
    return null;
  }
}

export async function fetchPwaWalletBalance(): Promise<PwaWalletBalance | null> {
  try {
    const result = await requestJson<{ success: boolean; data: PwaWalletBalance }>(
      `${TELEGRAM_API_BASE}/api/pwa/wallet/balance`
    );
    return result.data;
  } catch (error: any) {
    if (error?.message?.includes('401')) {
      return null;
    }
    return null;
  }
}

export async function fetchAgentWallet(): Promise<AgentWallet | null> {
  try {
    const result = await requestJson<{ success: boolean; data: AgentWallet }>(
      `${TELEGRAM_API_BASE}/api/agent/wallet`
    );
    return result.data;
  } catch (error: any) {
    if (error?.message?.includes('401') || error?.message?.includes('404')) {
      return null;
    }
    return null;
  }
}

export async function fetchAgentWalletBalance(): Promise<PwaWalletBalance | null> {
  try {
    const result = await requestJson<{ success: boolean; data: PwaWalletBalance }>(
      `${TELEGRAM_API_BASE}/api/agent/wallet/balance`
    );
    return result.data;
  } catch (error: any) {
    if (error?.message?.includes('401')) {
      return null;
    }
    return null;
  }
}

export async function fetchAgentHoldings(): Promise<PwaHolding[]> {
  const result = await requestJson<{ success: boolean; data: PwaHolding[] }>(
    `${TELEGRAM_API_BASE}/api/agent/holdings`
  );
  return result.data;
}

export async function fetchAgentSwaps(): Promise<AgentSwapEvent[]> {
  const result = await requestJson<{ success: boolean; data: AgentSwapEvent[] }>(
    `${TELEGRAM_API_BASE}/api/agent/swaps`
  );
  return result.data;
}

export async function fetchAgentTvlSummary(): Promise<AgentTvlSummary | null> {
  try {
    return await requestJson<AgentTvlSummary>(`${TELEGRAM_API_BASE}/api/agent/tvl`);
  } catch {
    return null;
  }
}

export async function fetchVerifiedAgents(): Promise<VerifiedAgent[]> {
  try {
    const result = await requestJson<{ success: boolean; data: VerifiedAgent[] }>(
      `${TELEGRAM_API_BASE}/api/agent/verified`
    );
    return result.data || [];
  } catch {
    return [];
  }
}

export async function fetchAgentStrategy(): Promise<AgentStrategyResponse | null> {
  try {
    const result = await requestJson<{ success: boolean; data: AgentStrategyResponse | null }>(
      `${TELEGRAM_API_BASE}/api/agent/strategy`
    );
    return result.data || null;
  } catch {
    return null;
  }
}

export async function saveAgentStrategy(params: {
  strategy: AgentStrategyConfig;
  enabled: boolean;
}): Promise<boolean> {
  const result = await requestJson<{ success: boolean; data?: { enabled: boolean } }>(
    `${TELEGRAM_API_BASE}/api/agent/strategy`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        strategy: params.strategy,
        enabled: params.enabled
      })
    }
  );
  return Boolean(result?.success);
}

export async function withdrawAgentSol(params: {
  toAddress: string;
  amountSol?: number;
  withdrawAll?: boolean;
}): Promise<PwaWithdrawResponse> {
  const result = await requestJson<{ success: boolean; data: PwaWithdrawResponse }>(
    `${TELEGRAM_API_BASE}/api/agent/withdraw-sol`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        toAddress: params.toAddress,
        amountSol: params.amountSol,
        withdrawAll: params.withdrawAll
      })
    }
  );
  return result.data;
}

export async function withdrawPwaSol(params: {
  toAddress: string;
  amountSol?: number;
  withdrawAll?: boolean;
}): Promise<PwaWithdrawResponse> {
  const result = await requestJson<{ success: boolean; data: PwaWithdrawResponse }>(
    `${TELEGRAM_API_BASE}/api/pwa/withdraw-sol`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(params)
    }
  );

  return result.data;
}

export async function deployPwaToken(params: {
  name: string;
  symbol: string;
  metadataUrl: string;
  logoUrl?: string | null;
  website?: string;
  twitter?: string;
  telegram?: string;
  devBuyAmountSol?: number;
  bundleBuyEnabled?: boolean;
  bundleWalletCount?: number;
  massCallEnabled?: boolean;
  displayUsername?: boolean;
}): Promise<PwaTokenDeployResponse> {
  const result = await requestJson<{ success: boolean; data: PwaTokenDeployResponse }>(
    `${TELEGRAM_API_BASE}/api/pwa/deploy-token`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(params)
    }
  );

  return result.data;
}

export async function swapPwa(params: {
  direction: 'buy' | 'sell';
  mintAddress: string;
  poolAddress: string;
  amountIn: string;
  minAmountOut?: string;
}): Promise<PwaSwapResponse> {
  const result = await requestJson<{ success: boolean; data: PwaSwapResponse }>(
    `${TELEGRAM_API_BASE}/api/pwa/swap`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(params)
    }
  );

  return result.data;
}

export async function fetchPwaHoldings(): Promise<PwaHolding[] | null> {
  try {
    const result = await requestJson<{ success: boolean; data: PwaHolding[] }>(
      `${TELEGRAM_API_BASE}/api/pwa/holdings`
    );
    return result.data;
  } catch (error: any) {
    if (error?.message?.includes('401')) {
      return null;
    }
    return null;
  }
}

export async function fetchPwaSwaps(limit: number = 10): Promise<PwaSwapEvent[] | null> {
  try {
    const result = await requestJson<{ success: boolean; data: PwaSwapEvent[] }>(
      `${TELEGRAM_API_BASE}/api/pwa/swaps?limit=${limit}`
    );
    return result.data;
  } catch (error: any) {
    if (error?.message?.includes('401')) {
      return null;
    }
    return null;
  }
}

export async function fetchPwaHarvestPositions(): Promise<PwaHarvestPosition[] | null> {
  try {
    const result = await requestJson<{ success: boolean; data: PwaHarvestPosition[] }>(
      `${TELEGRAM_API_BASE}/api/pwa/harvest/positions`
    );
    return result.data;
  } catch (error: any) {
    if (error?.message?.includes('401')) {
      return null;
    }
    return null;
  }
}

export async function fetchPwaFeaturedSlots(): Promise<PwaFeaturedSlots | null> {
  try {
    const result = await requestJson<{ success: boolean; data: PwaFeaturedSlots }>(
      `${TELEGRAM_API_BASE}/api/pwa/featured-slots`
    );
    return result.data;
  } catch (error: any) {
    if (error?.message?.includes('401')) {
      return null;
    }
    return null;
  }
}

export async function createPwaFeaturedSlot(params: {
  slotType: 'primary' | 'secondary_1' | 'secondary_2' | 'secondary_3';
  mintAddress: string;
  durationHours: 4 | 8 | 12 | 24;
}): Promise<PwaFeaturedSlot> {
  const result = await requestJson<{ success: boolean; data: PwaFeaturedSlot }>(
    `${TELEGRAM_API_BASE}/api/pwa/featured-slots`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(params)
    }
  );
  return result.data;
}

export async function fetchPwaFeaturedCarousel(): Promise<PwaCarouselSlots | null> {
  try {
    const result = await requestJson<{ success: boolean; data: PwaCarouselSlots }>(
      `${TELEGRAM_API_BASE}/api/pwa/featured-carousel`
    );
    return result.data;
  } catch (error: any) {
    if (error?.message?.includes('401')) {
      return null;
    }
    return null;
  }
}

export async function createPwaFeaturedCarousel(params: {
  mintAddress: string;
}): Promise<{ mintAddress: string; endsAt: string; paidSol: number }> {
  const result = await requestJson<{ success: boolean; data: { mintAddress: string; endsAt: string; paidSol: number } }>(
    `${TELEGRAM_API_BASE}/api/pwa/featured-carousel`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(params)
    }
  );
  return result.data;
}

export async function harvestPwaPosition(positionMint: string): Promise<PwaHarvestResult> {
  const result = await requestJson<{ success: boolean; data: PwaHarvestResult }>(
    `${TELEGRAM_API_BASE}/api/pwa/harvest`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ positionMint })
    }
  );
  return result.data;
}

export async function fetchPwaWsolBalance(): Promise<PwaWsolBalance | null> {
  try {
    const result = await requestJson<{ success: boolean; data: PwaWsolBalance }>(
      `${TELEGRAM_API_BASE}/api/pwa/wallet/wsol-balance`
    );
    return result.data;
  } catch (error: any) {
    if (error?.message?.includes('401')) {
      return null;
    }
    return null;
  }
}

export async function fetchPwaHarvestWsolBalance(): Promise<PwaWsolBalance | null> {
  try {
    const result = await requestJson<{ success: boolean; data: PwaWsolBalance }>(
      `${TELEGRAM_API_BASE}/api/pwa/harvest/wsol-balance`
    );
    return result.data;
  } catch (error: any) {
    if (error?.message?.includes('401')) {
      return null;
    }
    return null;
  }
}

export async function unwrapPwaWsol(): Promise<PwaUnwrapWsolResult> {
  const result = await requestJson<{ success: boolean; data: PwaUnwrapWsolResult }>(
    `${TELEGRAM_API_BASE}/api/pwa/unwrap-wsol`,
    {
      method: 'POST'
    }
  );
  return result.data;
}

export async function unwrapPwaHarvestWsol(): Promise<PwaUnwrapWsolResult> {
  const result = await requestJson<{ success: boolean; data: PwaUnwrapWsolResult }>(
    `${TELEGRAM_API_BASE}/api/pwa/harvest/unwrap-wsol`,
    {
      method: 'POST'
    }
  );
  return result.data;
}

export async function withdrawPwaHarvestSol(params: {
  toAddress?: string;
  toMainWallet?: boolean;
  amountSol?: number;
  withdrawAll?: boolean;
}): Promise<PwaWithdrawResponse> {
  const result = await requestJson<{ success: boolean; data: PwaWithdrawResponse }>(
    `${TELEGRAM_API_BASE}/api/pwa/harvest/withdraw-sol`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(params)
    }
  );
  return result.data;
}

export async function fetchPwaBundleWallets(
  count: number,
  options?: { reset?: boolean; details?: boolean }
): Promise<PwaBundleWalletsResult> {
  const result = await requestJson<{ success: boolean; data: PwaBundleWalletsResult }>(
    `${TELEGRAM_API_BASE}/api/pwa/bundle-wallets`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ count, reset: options?.reset, details: options?.details })
    }
  );
  return result.data;
}

export async function fetchPwaTokenBalance(mint: string): Promise<PwaTokenBalance> {
  const result = await requestJson<{ success: boolean; data: PwaTokenBalance }>(
    `${TELEGRAM_API_BASE}/api/pwa/token-balance?mint=${encodeURIComponent(mint)}`
  );
  return result.data;
}

export async function sellPwaBundleWallet(params: {
  walletAddress: string;
  mintAddress: string;
}): Promise<PwaBundleWalletSellResult> {
  const result = await requestJson<{ success: boolean; data: PwaBundleWalletSellResult }>(
    `${TELEGRAM_API_BASE}/api/pwa/bundle-wallets/sell`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(params)
    }
  );
  return result.data;
}

export async function sellAllPwaBundleWallets(): Promise<PwaBundleWalletSellAllResult> {
  const result = await requestJson<{ success: boolean; data: PwaBundleWalletSellAllResult }>(
    `${TELEGRAM_API_BASE}/api/pwa/bundle-wallets/sell-all`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );
  return result.data;
}

export async function withdrawPwaBundleWallet(params: {
  walletAddress: string;
  toAddress?: string;
}): Promise<PwaBundleWalletWithdrawResult> {
  const result = await requestJson<{ success: boolean; data: PwaBundleWalletWithdrawResult }>(
    `${TELEGRAM_API_BASE}/api/pwa/bundle-wallets/withdraw`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(params)
    }
  );
  return result.data;
}

export async function withdrawAllPwaBundleWallets(params?: {
  toAddress?: string;
}): Promise<PwaBundleWalletWithdrawAllResult> {
  const result = await requestJson<{ success: boolean; data: PwaBundleWalletWithdrawAllResult }>(
    `${TELEGRAM_API_BASE}/api/pwa/bundle-wallets/withdraw-all`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(params || {})
    }
  );
  return result.data;
}

export async function fetchPwaBundleScaleOutRule(mintAddress: string): Promise<PwaBundleScaleOutRule | null> {
  const result = await requestJson<{ success: boolean; data: PwaBundleScaleOutRule | null }>(
    `${TELEGRAM_API_BASE}/api/pwa/bundle-scale-out?mintAddress=${encodeURIComponent(mintAddress)}`
  );
  return result.data ?? null;
}

export async function updatePwaBundleScaleOutRule(params: {
  mintAddress: string;
  targetMarketCapUsd: number;
  scaleOutPercent: 10 | 20 | 30;
  enabled: boolean;
}): Promise<PwaBundleScaleOutRule> {
  const result = await requestJson<{ success: boolean; data: PwaBundleScaleOutRule }>(
    `${TELEGRAM_API_BASE}/api/pwa/bundle-scale-out`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(params)
    }
  );
  return result.data;
}

export async function fetchSnsPrimaryDomain(): Promise<string | null> {
  try {
    const result = await requestJson<{ success: boolean; data: { domain: string | null } }>(
      `${TELEGRAM_API_BASE}/api/pwa/sns/primary-domain`
    );
    return result.data?.domain ?? null;
  } catch (error: any) {
    if (error?.message?.includes('401')) {
      return null;
    }
    return null;
  }
}

export async function resolveSnsDomainForAddress(address: string): Promise<string | null> {
  const result = await requestJson<{ success: boolean; data: { domain: string | null } }>(
    `${TELEGRAM_API_BASE}/api/pwa/sns/resolve`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ address })
    }
  );

  return result.data?.domain ?? null;
}

export async function checkSnsDomainAvailability(name: string): Promise<SnsAvailabilityResult> {
  const result = await requestJson<{ success: boolean; data: SnsAvailabilityResult }>(
    `${TELEGRAM_API_BASE}/api/pwa/sns/check`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ name })
    }
  );

  return result.data;
}

export async function registerSnsDomain(name: string): Promise<SnsRegisterResult> {
  const result = await requestJson<{ success: boolean; data: SnsRegisterResult }>(
    `${TELEGRAM_API_BASE}/api/pwa/sns/register`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ name })
    }
  );

  return result.data;
}

export async function logoutTelegram(): Promise<void> {
  try {
    await requestJson(`${TELEGRAM_API_BASE}/api/telegram/logout`, {
      method: 'POST'
    });
  } catch (error) {
    // Ignore logout errors
  } finally {
    setTelegramSession(null);
  }
}

export async function exchangeTelegramHandoff(code: string): Promise<string | null> {
  try {
    const result = await requestJson<{ success: boolean; sessionId: string }>(
      `${TELEGRAM_API_BASE}/api/telegram/handoff?code=${encodeURIComponent(code)}`
    );
    return result.sessionId;
  } catch (error: any) {
    if (error?.message?.includes('404')) {
      return null;
    }
    return null;
  }
}
